/**
 * Enterprise Systems Main Page
 *
 * Main listing page for enterprise systems with:
 * - Header with create button
 * - Search and filter components
 * - System cards grid/list
 * - Pagination
 * - Real-time data fetching with React Query
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import EnterpriseSystemsList from '../components/enterprise-systems/EnterpriseSystemsList';
import EnterpriseSystemFilters, {
  FilterState,
} from '../components/enterprise-systems/EnterpriseSystemFilters';
import { getEnterpriseSystems, type EnterpriseSystem } from '../lib/api/enterpriseSystemsApi';
import { SESSION_CACHE } from '../lib/queryCache';

const EnterpriseSystemsPage: React.FC = () => {
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    searchText: '',
    categories: [],
    vendors: [],
    statuses: [],
    qualityRange: [0, 100],
  });

  // Fetch enterprise systems
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['enterprise-systems'],
    queryFn: () => getEnterpriseSystems(),
    ...SESSION_CACHE,
  });

  /**
   * Apply client-side filtering
   * (In production, this should be server-side)
   */
  const filteredSystems = useMemo(() => {
    if (!data?.systems) return [];

    let filtered = [...data.systems];

    // Text search
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(
        (system) =>
          system.system_name.toLowerCase().includes(searchLower) ||
          system.vendor.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter((system) =>
        filters.categories.includes(system.category)
      );
    }

    // Vendor filter
    if (filters.vendors.length > 0) {
      filtered = filtered.filter((system) =>
        filters.vendors.includes(system.vendor)
      );
    }

    // Status filter
    if (filters.statuses.length > 0) {
      filtered = filtered.filter((system) =>
        filters.statuses.includes(system.status)
      );
    }

    // Quality range filter
    const minQuality = filters.qualityRange[0] / 100;
    const maxQuality = filters.qualityRange[1] / 100;
    filtered = filtered.filter(
      (system) =>
        system.quality_score >= minQuality &&
        system.quality_score <= maxQuality
    );

    return filtered;
  }, [data?.systems, filters]);

  /**
   * Extract unique vendors from all systems
   */
  const availableVendors = useMemo(() => {
    if (!data?.systems) return [];
    const vendors = new Set(data.systems.map((s) => s.vendor));
    return Array.from(vendors).sort();
  }, [data?.systems]);

  /**
   * Navigate to create page
   */
  const handleCreateSystem = () => {
    navigate('/enterprise-systems/new');
  };

  /**
   * Navigate to system detail
   */
  const handleSystemClick = (system: EnterpriseSystem) => {
    navigate(`/enterprise-systems/${system.system_id}`);
  };

  /**
   * Refresh data
   */
  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header — constrained to the same max-width as the body so the top and
          bottom sections align at the same width. */}
      <div className="max-w-screen-2xl mx-auto px-6 pt-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                Enterprise Systems
              </h1>
              <p className="mt-2 text-neutral-600">
                Browse and manage enterprise system documentation catalog
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isFetching}
                className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Refresh systems"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
                />
                <span className="font-medium">Refresh</span>
              </button>

              {/* Create Button */}
              <button
                onClick={handleCreateSystem}
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add System</span>
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          {data && (
            <div className="mt-6 grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <p className="text-sm text-blue-700 font-medium">
                  Total Systems
                </p>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  {data.total}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium">Published</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {data.systems.filter((s) => s.status === 'published').length}
                </p>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
                <p className="text-sm text-yellow-700 font-medium">
                  In Progress
                </p>
                <p className="text-2xl font-bold text-yellow-900 mt-1">
                  {
                    data.systems.filter(
                      (s) => s.status === 'researching' || s.status === 'quality_review'
                    ).length
                  }
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p className="text-sm text-purple-700 font-medium">
                  Avg Quality
                </p>
                <p className="text-2xl font-bold text-purple-900 mt-1">
                  {data.systems.length > 0
                    ? Math.round(
                        (data.systems.reduce(
                          (sum, s) => sum + s.quality_score,
                          0
                        ) /
                          data.systems.length) *
                          100
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="mb-8">
          <EnterpriseSystemFilters
            filters={filters}
            onFiltersChange={setFilters}
            availableVendors={availableVendors}
          />
        </div>

        {/* Systems List */}
        <EnterpriseSystemsList
          systems={filteredSystems}
          loading={isLoading}
          error={error as Error}
          onSystemClick={handleSystemClick}
          emptyMessage={
            filters.searchText ||
            filters.categories.length > 0 ||
            filters.vendors.length > 0 ||
            filters.statuses.length > 0 ||
            filters.qualityRange[0] > 0 ||
            filters.qualityRange[1] < 100
              ? 'No systems match your filters. Try adjusting your search criteria.'
              : 'No enterprise systems found. Create your first system to get started.'
          }
        />
      </div>
    </div>
  );
};

export default EnterpriseSystemsPage;
